import React, { Fragment } from 'react'
import Head from 'next/head'
import { Profile, ProfileContent } from '@dashboard/lib/types'

import PublicProfile from '@dashboard/components/PublicProfile'
import Container from '@dashboard/components/Container'
import Nav from '@dashboard/components/ComparisonNav'

import { getContentRequest } from '@dashboard/store/actions/profile/public/content'
import { loadCelebritiesRequest } from '@dashboard/store/actions/celebrities/public'
import { loadCelebrityRequest } from '@dashboard/store/actions/celebrities/public/show'

import { ENRICHMENT_API_URL, MARKETING_APP_URL } from '@dashboard/lib/constants'
import { fullName } from '@dashboard/lib/profile'
import FamousPeople from '@dashboard/components/FamousPeople'
import { NextPageContext } from 'next'

import css from './styles.module.scss'

export interface OwnProps {
  query: Record<string, unknown>
  pathname: string
  profileId: string
  profileStringArray: string[]
  isPermalink: boolean
  celebrity: Profile | null
  celebrities: Profile[] | null
  content: ProfileContent
  ogImage: string | null
}

export interface StateProps {}

export interface DispatchProps {
  loadCelebrity: (id: string) => void
}

interface State {}

type Props = StateProps & DispatchProps & OwnProps

export default class ProfileShow extends React.Component<Props, State> {
  static async getInitialProps(ctx: NextPageContext): Promise<OwnProps> {
    const { query, pathname } = ctx
    const profileId = query.profileId as string
    const profileStringArray = (profileId as string).split('-')
    const isPermalink = profileStringArray.length > 5

    const ogImage = `${ENRICHMENT_API_URL}/resources/og_images/${profileId}`
    if (isPermalink) {
      if (ctx.res) {
        ctx.res.writeHead(301, {
          Location: `${MARKETING_APP_URL}/personality-test`,
        })
        ctx.res.end()
      } else {
        window.location.href = `${MARKETING_APP_URL}/personality-test`
      }

      return {
        query,
        pathname,
        profileId,
        profileStringArray,
        isPermalink,
        celebrity: null,
        celebrities: null,
        ogImage: ogImage,
        content: {},
      }
    }

    const celebrity =
      (await loadCelebrityRequest(profileId).catch(() => null))?.data.data ||
      null
    const celebrities =
      (await loadCelebritiesRequest().catch(() => null))?.data.data || null

    const content =
      (await getContentRequest(profileId).catch(() => null))?.data.data || {}

    return {
      query,
      pathname,
      profileId,
      profileStringArray,
      isPermalink,
      celebrity,
      celebrities,
      ogImage,
      content,
    }
  }

  render() {
    const { isPermalink, celebrity, ogImage } = this.props
    const defaultOgImage =
      'https://www.crystalknows.com/hubfs/WebsiteApril2019/og-image.png'
    const name = celebrity ? fullName(celebrity) : ''
    const title = celebrity
      ? `${name}'s Personality Type - Enneagram, 16-Personality (based on types by Jung, Myers, & Briggs), and DISC`
      : 'Personality Types on Crystal - Enneagram, 16-Personality (based on types by Jung, Myers, & Briggs), and DISC'
    const celebNotFound = !isPermalink && celebrity === null

    if (celebNotFound)
      return (
        <FamousPeople
          headerText="Looks like that person isn't on Crystal yet!"
          subText="Explore other famous people below."
          ogTitle="Celebrity Not Found"
          ogDescription="Explore other famous people to learn their DISC, Enneagram, and 16-Personality (based on types by Jung, Myers, & Briggs) types."
          hideShareActions
        />
      )

    return (
      <Fragment>
        <Head>
          <title>{title}</title>
          <meta property="og:title" content={title} />
          <meta
            property="og:description"
            content={`Create a free Crystal account to view ${name}'s personality.`}
          />
          <meta
            property="og:image"
            content={
              ogImage || (celebrity && celebrity.photo_url) || defaultOgImage
            }
          />
          <meta
            property="description"
            content={` See ${name}'s personality profile on Crystal, including DISC, 16-Personality (based on types by Jung, Myers, & Briggs), Enneagram, motivations, values, and more. `}
          />
        </Head>
        <Container>
          <Nav showLogin />
        </Container>
        {this.renderPublicProfile()}
      </Fragment>
    )
  }

  renderPublicProfile() {
    const { celebrity, content, celebrities } = this.props

    if (!celebrity) return null

    return (
      <PublicProfile
        profile={celebrity}
        signUpCTAText="Sign up free"
        signUpCTALink={`/app/register?profile_id=${celebrity.id}`}
        content={content}
        profileId={celebrity.id}
        otherCelebritiesResult={celebrities || undefined}
      />
    )
  }

  renderFailureState() {
    return (
      <Container>
        <h1 className={css.errorTitle}>Failed loading profile.</h1>
        <p className={css.errorText}>
          We&apos;re sorry, there was a problem loading this profile.
        </p>
        <p className={css.errorText}>
          Please refresh the page or try loading another profile.
        </p>
      </Container>
    )
  }
}
